import classNames from 'classnames';
import { assessCancellationDescription } from './CancelSubscription.functions';
import {
  ArrowLeftIcon,
  BanknotesIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  PauseIcon,
  RectangleGroupIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Button, Modal, Radio, Textarea } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { isScheduledOrActivePlanPause } from 'src/functions/planPause.functions';
import { useSubscriptionManagerContext } from '../../../SubscriptionManager.context';
import { tCancellationReason } from '../../../SubscriptionManager.types';
import { useCancelSubscriptionContext } from './CancelSubscription.context';
import { useSubscriptionContext } from '../../../../../../../providers/SubscriptionProvider/Subscription.context';
import { CANCELLATION_DESCRIPTION_MIN_LENGTH } from '../../../SubscriptionManager.constants';

type tReasonIcon = typeof BanknotesIcon;

export default function CancelForm() {
  const {
    handleKeepSubscription,
    handleSelectReason,
    isLoadingReasonSelection,
    setShowForm,
  } = useCancelSubscriptionContext();
  const { t } = useTranslation('settings/SubscriptionManager');
  const { cancelSubscriptionFormik } = useSubscriptionManagerContext();
  const { subscription } = useSubscriptionContext();

  if (!cancelSubscriptionFormik) return <></>;

  const getDescriptionPlaceholder = (reason: tCancellationReason): string => {
    const placeholderKeyMap: Record<tCancellationReason, string> = {
      pause_operation:
        'cancelSubscription.form.descriptionPlaceholder.pauseOperation',
      price_too_high:
        'cancelSubscription.form.descriptionPlaceholder.priceTooHigh',
      technical_problems:
        'cancelSubscription.form.descriptionPlaceholder.technicalProblems',
      plan_not_suitable:
        'cancelSubscription.form.descriptionPlaceholder.planNotSuitable',
      not_seeing_results:
        'cancelSubscription.form.descriptionPlaceholder.notSeeingResults',
      other: 'cancelSubscription.form.descriptionPlaceholder.default',
    };
    const key = placeholderKeyMap[reason] || placeholderKeyMap.other;
    return t(key);
  };

  // The icon names the reason at a glance, so a scanning customer does not have to
  // read six similar sentences to find theirs. Order is unchanged: it is the order
  // the server's reason codes and the funnel reports already use.
  const cancellationReasons: Array<{
    value: tCancellationReason;
    labelKey: string;
    icon: tReasonIcon;
  }> = [
    {
      value: 'pause_operation',
      labelKey: 'cancelSubscription.form.reasons.pauseOperation',
      icon: PauseIcon,
    },
    {
      value: 'price_too_high',
      labelKey: 'cancelSubscription.form.reasons.priceTooHigh',
      icon: BanknotesIcon,
    },
    {
      value: 'technical_problems',
      labelKey: 'cancelSubscription.form.reasons.technicalProblems',
      icon: WrenchScrewdriverIcon,
    },
    {
      value: 'plan_not_suitable',
      labelKey: 'cancelSubscription.form.reasons.planNotSuitable',
      icon: RectangleGroupIcon,
    },
    {
      value: 'not_seeing_results',
      labelKey: 'cancelSubscription.form.reasons.notSeeingResults',
      icon: ChartBarIcon,
    },
    {
      value: 'other',
      labelKey: 'cancelSubscription.form.reasons.other',
      icon: ChatBubbleLeftRightIcon,
    },
  ];

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      // Validate form before proceeding
      const errors = await cancelSubscriptionFormik.validateForm();

      // If there are validation errors, mark fields as touched and don't proceed
      if (Object.keys(errors).length > 0) {
        // Mark all error fields as touched to show validation messages
        Object.keys(errors).forEach((field) => {
          cancelSubscriptionFormik.setFieldTouched(
            field as keyof typeof errors,
            true,
          );
        });
        return;
      }

      const { cancellationReason, description } =
        cancelSubscriptionFormik.values;

      const selectedReason = cancellationReasons.find(
        (reason) => reason.value === cancellationReason,
      );

      // A value that is not one of the reasons above is not submittable; say so
      // instead of leaving a button that does nothing.
      if (!selectedReason) {
        cancelSubscriptionFormik.setFieldTouched(
          'cancellationReason',
          true,
          false,
        );
        cancelSubscriptionFormik.setFieldError(
          'cancellationReason',
          t('cancelSubscription.form.validation.required'),
        );
        return;
      }

      const reasonLabel = t(selectedReason.labelKey);

      // schedule-cancelation records the reason as the customer read it, so the
      // form carries the label from here on. Which offer, if any, applies is the
      // server's answer to the reason — the form no longer decides it.
      cancelSubscriptionFormik.setValues({
        ...cancelSubscriptionFormik.values,
        cancellationReason: reasonLabel,
      });

      await handleSelectReason({
        reason: selectedReason.value,
        reasonLabel,
        detailsText: description,
      });
    } catch (error) {
      console.error('Form validation error:', error);
      // Form validation failed, errors are already handled by Formik
    }
  };

  return (
    // The form is the modal box's only real child, so it has to BE the flex column,
    // not sit inside one. As a plain block it grew to its content height inside a box
    // capped at 92vh, and `overflow-hidden` on the box silently clipped the footer —
    // the taller the reason list got, the more of the buttons disappeared. `min-h-0`
    // is what lets it shrink below its content so the body can scroll instead.
    <form
      id="cancelSubscriptionForm"
      onSubmit={handleFormSubmit}
      noValidate
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <Modal.Body className="flex flex-1 flex-col overflow-y-auto pb-0">
        <div className="flex flex-col gap-6 px-10 pb-2 pt-5">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <ChatBubbleLeftRightIcon
                aria-hidden="true"
                className="h-7 w-7 text-primary"
              />
            </span>
            <h2 className="text-3xl font-bold leading-tight text-neutral-800">
              {t('cancelSubscription.form.heading')}
            </h2>
            <p className="text-base text-neutral-600">
              {t('cancelSubscription.form.subheading')}
            </p>
          </div>
          {/* Cancelling ends only a scheduled or active pause. A paid early
              resume is already over for the customer (PD12): nothing to warn about. */}
          {isScheduledOrActivePlanPause(subscription.planPause) ? (
            <div role="note" className="alert alert-warning text-sm">
              {t('cancelSubscription.form.pauseCancellationNotice')}
            </div>
          ) : null}

          <div className="flex flex-col gap-3">
            {cancellationReasons.map((reason) => {
              const ReasonIcon = reason.icon;
              const isSelected =
                cancelSubscriptionFormik.values.cancellationReason ===
                reason.value;
              const length = cancelSubscriptionFormik.values.description.length;
              const isLongEnough = length >= CANCELLATION_DESCRIPTION_MIN_LENGTH;
              // Asked only once the length gate is satisfied. Before that the customer is
              // still mid-sentence, and judging an unfinished thought would be the same
              // scolding the removed red error used to do.
              const asksForDetail =
                isLongEnough &&
                assessCancellationDescription(
                  cancelSubscriptionFormik.values.description,
                ).isLowInformation;
              const isReady = isLongEnough && !asksForDetail;

              return (
                <div key={reason.value} className="flex flex-col gap-3">
                  <label
                    className={classNames(
                      'flex cursor-pointer items-center gap-4 rounded-lg border px-4 py-5 transition-colors',
                      {
                        'border-primary bg-primary/5': isSelected,
                        'border-neutral-400 hover:border-neutral-500':
                          !isSelected,
                      },
                    )}
                    data-testid={`cancellation-reason-${reason.value}`}
                  >
                    <ReasonIcon
                      aria-hidden="true"
                      className={classNames('h-6 w-6 flex-shrink-0', {
                        'text-primary': isSelected,
                        'text-neutral-600': !isSelected,
                      })}
                    />
                    <span
                      className={classNames('flex-1 text-base leading-normal', {
                        'text-primary font-semibold': isSelected,
                        'text-neutral-700': !isSelected,
                      })}
                    >
                      {t(reason.labelKey)}
                    </span>
                    <Radio
                      name="cancellationReason"
                      value={reason.value}
                      checked={isSelected}
                      onChange={cancelSubscriptionFormik.handleChange}
                      color="primary"
                      className="flex-shrink-0 w-5 h-5"
                      disabled={isLoadingReasonSelection}
                      data-testid={`cancellation-reason-radio-${reason.value}`}
                    />
                  </label>
                  {isSelected && (
                    // The accent rail and the visible label tie the comment to the row
                    // it belongs to; before, the only prompt was placeholder text that
                    // vanished as soon as the customer started typing.
                    <div className="flex flex-col gap-2 border-l-2 border-primary pl-3 ml-3">
                      <label
                        htmlFor="description"
                        className="text-sm text-neutral-700"
                      >
                        {t('cancelSubscription.form.descriptionLabel')}
                      </label>
                      <Textarea
                        id="description"
                        name="description"
                        value={cancelSubscriptionFormik.values.description}
                        onChange={cancelSubscriptionFormik.handleChange}
                        onBlur={cancelSubscriptionFormik.handleBlur}
                        placeholder={getDescriptionPlaceholder(reason.value)}
                        disabled={isLoadingReasonSelection}
                        aria-describedby="cancellation-description-counter"
                        className="min-h-[120px] resize-none border border-neutral-400 rounded-[4px]"
                        data-testid="cancellation-description-textarea"
                      />
                      {/* The only feedback on this field. The red "please provide a
                          description" error was removed deliberately: the customer can
                          already see the count and that Continuar is disabled, so the
                          error only scolded them for not having finished typing yet.
                          This must never say "ready" before the schema agrees, which is
                          why both read CANCELLATION_DESCRIPTION_MIN_LENGTH. */}
                      <div
                        id="cancellation-description-counter"
                        aria-live="polite"
                        className={classNames('text-sm', {
                          'text-success-text': isReady,
                          'text-neutral-600': !isReady,
                        })}
                        data-testid="cancellation-description-counter"
                      >
                        {/* Three states, and the middle one is the point: the comment is
                            long enough to pass, so Continuar stays enabled and nobody is
                            trapped — we simply ask rather than congratulate. */}
                        {asksForDetail
                          ? t('cancelSubscription.form.descriptionAskForDetail')
                          : isReady
                            ? t('cancelSubscription.form.descriptionReady', {
                                count: length,
                              })
                            : t('cancelSubscription.form.descriptionHint', {
                                count: length,
                                min: CANCELLATION_DESCRIPTION_MIN_LENGTH,
                              })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-sm text-neutral-600">
            {t('cancelSubscription.form.cancellationNote')}
          </p>

          {cancelSubscriptionFormik.touched.cancellationReason &&
          cancelSubscriptionFormik.errors.cancellationReason ? (
            <div className="text-error text-sm">
              {cancelSubscriptionFormik.errors.cancellationReason}
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Actions className="m-0 flex flex-shrink-0 flex-row items-center justify-between gap-3 border-t border-neutral-200 bg-base-100 px-10 py-5">
        <Button
          type="button"
          color="ghost"
          size="lg"
          className="rounded-lg font-normal text-neutral-700"
          onClick={() => setShowForm(false)}
          disabled={isLoadingReasonSelection}
          data-testid="cancel-form-back-button"
        >
          <ArrowLeftIcon aria-hidden="true" className="mr-1 h-4 w-4" />
          {t('cancelSubscription.form.backButton')}
        </Button>
        <div className="flex flex-row items-center gap-3">
        <Button
          color="primary"
          className="min-w-[200px] rounded-lg"
          size="lg"
          onClick={handleKeepSubscription}
          disabled={isLoadingReasonSelection}
          data-testid="cancel-form-keep-button"
        >
          {t('cancelSubscription.form.keepButton')}
        </Button>
        <Button
          type="submit"
          form="cancelSubscriptionForm"
          color="ghost"
          variant="outline"
          className="min-w-[160px] rounded-lg border-neutral-700 bg-base-100 text-neutral-700"
          size="lg"
          loading={isLoadingReasonSelection}
          disabled={
            isLoadingReasonSelection || !cancelSubscriptionFormik.isValid
          }
          data-testid="cancel-form-continue-button"
        >
          {t('cancelSubscription.form.continueButton')}
        </Button>
        </div>
      </Modal.Actions>
    </form>
  );
}
